import React from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const SortingComponent = ({
    title,
    data,
    keyProp,
    onSort,
    align = 'left',
    headerTone = 'dark',
}: any) => {
    const isLightHeader = headerTone === 'light';
    const labelColor = isLightHeader ? '#494646' : '#ffffff';
    const iconColor = isLightHeader ? '#494646' : '#ffffff';

    function inferType(value: any) {
        if (typeof value === 'number') {
            return 'number';
        } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
            // Check for DD/MM/YYYY format first before checking if it's a number
            return 'string';
        } else if (!isNaN(parseFloat(value))) {
            return 'float';
        } else if (/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(value)) {
            return 'date';
        } else {
            return 'string';
        }
    }

    const riskLevels: { [key: string]: number } = {
        'Low Risk': 1,
        'Low to Moderate Risk': 2,
        'Moderate Risk': 3,
        'Moderately High risk': 4,
        'High Risk': 5,
        'Very High Risk': 6
    };

    const interestRateFrequency: { [key: string]: number } = {
        'Days': 1,
        'MONTHLY': 2,
        'HALFYEARLY': 3,
        'YEARLY': 4,
        'CUMULATIVEDEPOSIT': 5,
    };

    // Helper function to parse DD/MM/YYYY date format
    const parseDDMMYYYY = (dateStr: string): number => {
        if (!dateStr || typeof dateStr !== 'string') return 0;
        // Check if it matches DD/MM/YYYY format
        const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (match) {
            const [, day, month, year] = match;
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).getTime();
        }
        return 0;
    };

    function sortArray(sortConfig: any) {
        const { key, order = 'asc' } = sortConfig;
        if (!Array.isArray(data) || data.length === 0) {
            return;
        }
        const sampleValue = data?.[0]?.[key];
        const type = key === 'riskLevel' ? 'riskLevel' : inferType(sampleValue);

        const sortedArray = [...data].sort((a, b) => {
            let comparison = 0;
            let aValue, bValue;

            if (type === 'riskLevel') {
                aValue = riskLevels[a[key]] || 0;
                bValue = riskLevels[b[key]] || 0;
                comparison = aValue - bValue;
            } else if (key === 'investmentFrequency') {
                aValue = interestRateFrequency[a[key]] || 0;
                bValue = interestRateFrequency[b[key]] || 0;
                comparison = aValue - bValue;
            } else if (key === 'createdAt') {
                aValue = new Date(a[key]).getTime();
                bValue = new Date(b[key]).getTime();
                comparison = aValue - bValue;
            } else if (type === 'number' || type === 'float') {
                aValue = parseFloat(a[key]) || 0;
                bValue = parseFloat(b[key]) || 0;
                // Handle floating point precision comparison
                const tolerance = 0.000001;
                if (Math.abs(aValue - bValue) < tolerance) {
                    comparison = 0;
                } else {
                    comparison = aValue - bValue;
                }
                // console.logumber/Float comparison:', aValue, bValue, comparison);
            } else if (type === 'string') {
                aValue = a[key] ?? '';
                bValue = b[key] ?? '';
                
                // Convert to string if not already
                aValue = String(aValue);
                bValue = String(bValue);
                
                // Check if both values are dates in DD/MM/YYYY format
                const aIsDate = /^\d{2}\/\d{2}\/\d{4}$/.test(aValue);
                const bIsDate = /^\d{2}\/\d{2}\/\d{4}$/.test(bValue);
                
                if (aIsDate && bIsDate) {
                    // Both are dates, parse and compare as timestamps
                    const aTimestamp = parseDDMMYYYY(aValue);
                    const bTimestamp = parseDDMMYYYY(bValue);
                    comparison = aTimestamp - bTimestamp;
                    // Debug log
                    if (key === 'transactionDate') {
                        console.log(`Comparing dates: ${aValue} (${aTimestamp}) vs ${bValue} (${bTimestamp}) = ${comparison}`);
                    }
                } else {
                    // Normal string comparison
                    comparison = aValue.localeCompare(bValue);
                }
            }

            return order === 'asc' ? comparison : -comparison;
        });

        if (typeof onSort === 'function') {
            onSort(sortedArray);
        }
    }
    const getJustifyClass = () => {
        if (align === 'right') return 'justify-end';
        if (align === 'center') return 'justify-center';
        return 'justify-start';
    };

    return (
        <div className={`flex items-center gap-1 w-full ${getJustifyClass()}`}>
            <span
                className="font-semibold text-sm leading-tight break-words"
                style={{ color: labelColor }}
            >
                {title.split('\n').map((line: string, index: number) => (
                    <React.Fragment key={index}>
                        {line}
                        {index < title.split('\n').length - 1 && <br />}
                    </React.Fragment>
                ))}
            </span>
            <div className='flex flex-col items-center justify-center flex-shrink-0 ml-1'>
                <div className='' >
                    <KeyboardArrowUpIcon 
                        onClick={() => sortArray({ key: keyProp, order: 'asc' })} 
                        className='cursor-pointer hover:opacity-80 transition-opacity' 
                        sx={{ fontSize: '16px', color: iconColor, marginBottom: '-6px' }}
                    />
                </div>
                <div className='' >
                    <KeyboardArrowDownIcon 
                        onClick={() => sortArray({ key: keyProp, order: 'desc' })} 
                        className='cursor-pointer hover:opacity-80 transition-opacity' 
                        sx={{ fontSize: '16px', color: iconColor, marginTop: '-6px' }}
                    />
                </div>
            </div>
        </div>
    );
}

export default SortingComponent;
